import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2, Check, X, GraduationCap, Users } from 'lucide-react';
import SEO, { SEO_CONFIG } from '@/components/SEO';

// Use relative path for API calls to avoid CORS issues
const API = '/api';

const RegisterPage = ({ onLogin }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get role from URL param (for "Become Instructor" flow)
  const initialRole = searchParams.get('role') || 'student';
  
  const [selectedRole, setSelectedRole] = useState(initialRole);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Update role when URL param changes
  useEffect(() => {
    const roleParam = searchParams.get('role');
    if (roleParam === 'instructor') {
      setSelectedRole('instructor');
    }
  }, [searchParams]);
  
  // Password strength calculator
  const passwordStrength = useMemo(() => {
    const pw = formData.password;
    if (!pw) return { score: 0, label: '', color: '' };
    
    let score = 0;
    if (pw.length >= 6) score++;
    if (pw.length >= 10) score++;
    if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
    if (/\d/.test(pw)) score++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(pw)) score++;
    
    if (score <= 1) return { score, label: 'Zayıf', color: 'bg-red-500' };
    if (score === 2) return { score, label: 'Orta', color: 'bg-orange-500' };
    if (score === 3) return { score, label: 'İyi', color: 'bg-yellow-500' };
    return { score, label: 'Güçlü', color: 'bg-green-500' };
  }, [formData.password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/register`, {
        ...formData,
        role: 'user',
        language: 'tr'
      });
      localStorage.setItem('token', response.data.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      onLogin(response.data.token, response.data.user);
      
      if (selectedRole === 'instructor') {
        // Instructor flow: redirect to application form
        toast.success('Hesap Oluşturuldu! 🎉', {
          description: 'Şimdi eğitmen başvuru formunu doldurun.'
        });
        navigate('/become-instructor');
      } else {
        // Student flow: redirect to home
        toast.success('Kayıt Başarılı! 🎉', {
          description: `Hoş geldiniz ${formData.full_name}! Wellness yolculuğunuz başlıyor.`
        });
        navigate('/');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Kayıt başarısız. Lütfen tekrar deneyin.';
      setError(errorMessage);
      toast.error('Kayıt Başarısız', {
        description: errorMessage
      });
      console.error('Register failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-100 to-pink-100 px-4 py-8">
      <SEO {...SEO_CONFIG.register} />
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-3">
          <div className="flex justify-center mb-4">
            <img 
              src="/icons/pilatika-logo.svg" 
              alt="Pilatika" 
              className="h-16 w-16"
              width="64"
              height="64"
            />
          </div>
          <h1 className="text-3xl font-bold text-center">Ücretsiz Üye Ol</h1>
          <p className="text-center text-gray-600">
            Pilatika'ya katılın ve hemen başlayın
          </p>
        </CardHeader>
        <CardContent>
          {/* Role Selection */}
          <div className="mb-6">
            <h2 className="text-sm font-semibold mb-3 block">Nasıl katılmak istiyorsunuz?</h2>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setSelectedRole('student')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  selectedRole === 'student'
                    ? 'border-purple-600 bg-purple-50 ring-2 ring-purple-200'
                    : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex flex-col items-center gap-2">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    selectedRole === 'student' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600'
                  }`}>
                    <GraduationCap className="h-6 w-6" />
                  </div>
                  <span className={`font-semibold text-sm ${
                    selectedRole === 'student' ? 'text-purple-700' : 'text-gray-700'
                  }`}>
                    Öğrenci
                  </span>
                  <span className="text-xs text-gray-500 text-center">
                    Ders satın al
                  </span>
                </div>
              </button>
              
              <button
                type="button"
                onClick={() => setSelectedRole('instructor')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  selectedRole === 'instructor'
                    ? 'border-purple-600 bg-purple-50 ring-2 ring-purple-200'
                    : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex flex-col items-center gap-2">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    selectedRole === 'instructor' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600'
                  }`}>
                    <Users className="h-6 w-6" />
                  </div>
                  <span className={`font-semibold text-sm ${
                    selectedRole === 'instructor' ? 'text-purple-700' : 'text-gray-700'
                  }`}>
                    Eğitmen
                  </span>
                  <span className="text-xs text-gray-500 text-center">
                    Ders sat ve kazan
                  </span>
                </div>
              </button>
            </div>
            
            {selectedRole === 'instructor' && (
              <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                <p className="text-xs text-purple-700">
                  💡 Eğitmen olarak kayıt olduktan sonra başvuru formunu dolduracaksınız. 
                  Başvurunuz admin tarafından onaylandığında kurs oluşturmaya başlayabilirsiniz.
                </p>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm">
                {error}
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="full_name" className="text-sm font-semibold">
                Ad Soyad <span className="text-red-500">*</span>
              </Label>
              <Input
                id="full_name"
                placeholder="Örn: Ahmet Yılmaz"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                required
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-semibold">
                E-posta <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="ornek@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-semibold">
                Şifre <span className="text-red-500">*</span>
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="En az 6 karakter"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={6}
                className="h-11"
              />
              
              {/* Password Strength Indicator */}
              {formData.password && (
                <div className="space-y-2">
                  <div className="flex gap-1">
                    {[...Array(4)].map((_, i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded-full transition-all ${
                          i < passwordStrength.score ? passwordStrength.color : 'bg-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-gray-600">
                    Şifre Gücü: <span className="font-semibold">{passwordStrength.label}</span>
                  </p>
                  <div className="text-xs text-gray-500 space-y-1">
                    <div className="flex items-center gap-1">
                      {formData.password.length >= 6 ? <Check className="h-3 w-3 text-green-600" /> : <X className="h-3 w-3 text-gray-400" />}
                      <span>En az 6 karakter</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {/\d/.test(formData.password) ? <Check className="h-3 w-3 text-green-600" /> : <X className="h-3 w-3 text-gray-400" />}
                      <span>En az bir rakam içermeli</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {/[A-Z]/.test(formData.password) ? <Check className="h-3 w-3 text-green-600" /> : <X className="h-3 w-3 text-gray-400" />}
                      <span>En az bir büyük harf içermeli</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 text-base font-semibold btn-primary"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Kayıt yapılıyor...
                </>
              ) : selectedRole === 'instructor' ? (
                <>👨‍🏫 Eğitmen Olarak Devam Et</>
              ) : (
                <>✨ Kayıt Ol</>
              )}
            </Button>

            <div className="text-center text-sm text-gray-600">
              Zaten hesabınız var mı?{' '}
              <Link to="/login" className="text-purple-600 hover:text-purple-800 font-semibold">
                Giriş Yap
              </Link>
            </div>
            
            <div className="text-center">
              <Link to="/" className="text-sm text-gray-500 hover:text-gray-700">
                ← Ana Sayfaya Dön
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default RegisterPage;
